import { useState, useMemo } from "react";
import debounce from "lodash/debounce";
import { Button } from "semantic-ui-react";
import { useTranslation } from "react-i18next";

import { SelectedCarrier } from "models/SelectedCarrier";
import CarrierQuickCapacity from "components/CarrierQuickCapacity/CarrierQuickCapacity";
import CarrierList from "components/CarrierList/List/CarrierList";
import CarrierMap from "components/CarrierMap/CarrierMap";
import Breadcrumbs from "components/Navigation/Breadcrumbs/Breadcrumbs";
import SearchBar from "components/Navigation/SearchBar/SearchBar";
import CarriersFilterBar from "./CarrierFilterBar/CarrierFilterBar";
import { inputDebounceDelay } from "hooks/constants";
import useCarriersFilter from "hooks/useCarriersFilter";
import useCarrierListColumns from "hooks/useCarrierListColumns";
import styles from "./Carriers.module.scss";

const Carriers = () => {
  const { t } = useTranslation();
  const [isQuickCapacityOpen, setIsQuickCapacityOpen] =
    useState<boolean>(false);
  const [selectedCarrier, setSelectedCarrier] =
    useState<SelectedCarrier | null>(null);
  const [cancelRequest, setCancelRequest] = useState<boolean>(false);
  const [isMapView, setIsMapView] = useState<boolean>(false);

  const [carriersFilter, setCarriersFilter] = useCarriersFilter();
  const [visibleColumns, setVisibleColumns] = useCarrierListColumns();

  const onSelectCarrier = (carrier: SelectedCarrier) => {
    setIsQuickCapacityOpen(true);
    setSelectedCarrier(carrier);
  };

  const debouncedSearchChange = useMemo(
    () =>
      debounce((searchText) => {
        setCancelRequest(false);
        setCarriersFilter((currentFilter) => ({
          ...currentFilter,
          search: searchText,
        }));
        setSelectedCarrier(null);
        setIsQuickCapacityOpen(false);
      }, inputDebounceDelay),
    [setCarriersFilter]
  );

  const onSearch = useMemo(
    () => (searchText: string) => {
      // cancelRequest gets passed to useGetCarriersInfinite to cancel in-flight requests as the user continues to type
      // Better UX and avoids user confusion about the results displayed
      setCancelRequest(true);
      debouncedSearchChange(searchText);
    },
    [debouncedSearchChange]
  );

  return (
    <div className={styles.carriersContainer}>
      <div className={styles.carriersSearchBar}>
        <Breadcrumbs />

        <SearchBar
          inputPlaceholder={t("carriers.searchPlaceholder")}
          onSearch={onSearch}
          autoTypeOfInputSearch={true}
          value={carriersFilter.search}
          disabled={carriersFilter.search.length === 0}
        />

        <Button.Group size="small">
          <Button
            type="button"
            icon="list"
            active={!isMapView}
            primary={!isMapView}
            title={t("carriers.listView")}
            aria-label={t("carriers.listView")}
            onClick={() => setIsMapView(false)}
          />
          <Button
            type="button"
            icon="map marker alternate"
            active={isMapView}
            primary={isMapView}
            title={t("carriers.mapView")}
            aria-label={t("carriers.mapView")}
            onClick={() => setIsMapView(true)}
          />
        </Button.Group>
      </div>

      <CarriersFilterBar
        carriersFilter={carriersFilter}
        setCarriersFilter={setCarriersFilter}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        resetQuickCapacity={() => {
          setIsQuickCapacityOpen(false);
          setSelectedCarrier(null);
        }}
      />

      {isMapView ? (
        <CarrierMap carriersFilter={carriersFilter} />
      ) : isQuickCapacityOpen && selectedCarrier ? (
        <CarrierQuickCapacity
          onSelectCarrier={onSelectCarrier}
          onQuickCapacityClose={() => setIsQuickCapacityOpen(false)}
          resetSelectedCarrier={() => setSelectedCarrier(null)}
          selectedCarrier={selectedCarrier}
          carriersFilter={carriersFilter}
          cancelRequest={cancelRequest}
        />
      ) : (
        <CarrierList
          onSelectCarrier={onSelectCarrier}
          selectedCarrier={selectedCarrier}
          carriersFilter={carriersFilter}
          visibleColumns={visibleColumns}
          cancelRequest={cancelRequest}
        />
      )}
    </div>
  );
};

export default Carriers;
